import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import pollService from '../services/pollService';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiMessageSquare, FiSend, FiUsers, FiClock } = FiIcons;

const ParticipantView = () => {
  const { id } = useParams();
  const [poll, setPoll] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inputsUsed, setInputsUsed] = useState(0);
  const [comments, setComments] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPollData();

    // Set up polling for real-time updates
    const interval = setInterval(fetchPollData, 3000);
    return () => clearInterval(interval);
  }, [id]);

  const fetchPollData = async () => {
    try {
      const data = await pollService.getPoll(id);
      setPoll(data);
      
      // Get used inputs from localStorage
      if (data.currentRound) {
        const sessionKey = `poll-${id}-session-${data.currentRound.id}`;
        const storedInputs = localStorage.getItem(sessionKey);
        if (storedInputs) {
          setInputsUsed(parseInt(storedInputs) || 0);
        } else {
          setInputsUsed(0);
        }
      }
      setError(null);
    } catch (error) {
      console.error('Error fetching poll:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const addPoint = async (categoryName) => {
    if (inputsUsed >= 5 || !poll.currentRound?.active || submitting) return;

    setSubmitting(true);
    try {
      await pollService.addInput(id, 'point', categoryName);
      const newInputsUsed = inputsUsed + 1;
      setInputsUsed(newInputsUsed);
      fetchPollData(); // Refresh poll data
      setError(null);
    } catch (error) {
      console.error('Error adding point:', error);
      setError(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const addComment = async (categoryName) => {
    const comment = comments[categoryName]?.trim();
    if (!comment || inputsUsed >= 5 || !poll.currentRound?.active || submitting) return;

    setSubmitting(true);
    try {
      await pollService.addInput(id, 'comment', categoryName, comment);
      const newInputsUsed = inputsUsed + 1;
      setInputsUsed(newInputsUsed);
      setComments({ ...comments, [categoryName]: '' });
      fetchPollData(); // Refresh poll data
      setError(null);
    } catch (error) {
      console.error('Error adding comment:', error);
      setError(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const updateComment = (categoryName, value) => {
    setComments({ ...comments, [categoryName]: value });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!poll) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-primary mb-4">Umfrage nicht gefunden</h2>
          {error && <p className="text-red-600">{error}</p>}
        </div>
      </div>
    );
  }

  const isRoundActive = poll.currentRound?.active;
  const remainingInputs = 5 - inputsUsed;

  return (
    <div className="min-h-screen bg-white">
      {/* Main Content */}
      <div className="container mx-auto px-4 py-6">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-primary mb-2">{poll.question}</h1>
          <div className="flex items-center justify-center space-x-6 text-sm text-primary">
            <div className="flex items-center space-x-1">
              <div className={`w-2 h-2 rounded-full ${isRoundActive ? 'bg-green-500' : 'bg-red-500'}`}></div>
              <span>{isRoundActive ? 'Runde aktiv' : 'Runde gestoppt'}</span>
            </div>
            <div className="flex items-center space-x-1">
              <SafeIcon icon={FiUsers} />
              <span>{poll.totalParticipants || 0} Teilnehmer</span>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Input Status */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6 border border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <SafeIcon icon={FiClock} className="text-primary" />
              <span className="font-medium text-primary">
                Noch {remainingInputs} von 5 Eingaben verfügbar
              </span>
            </div>
            <div className="w-32 bg-gray-200 rounded-full h-2">
              <div 
                className="bg-primary h-2 rounded-full transition-all duration-300"
                style={{ width: `${(inputsUsed / 5) * 100}%` }}
              ></div>
            </div>
          </div>
        </div>

        {!isRoundActive && (
          <div className="mb-6">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
              <p className="text-yellow-800">
                Die Umfrage ist derzeit gestoppt. Warten Sie auf den Host, um fortzufahren.
              </p>
            </div>
          </div>
        )}

        {/* Categories Grid */}
        <div className={`grid gap-6 ${poll.categories.length <= 2 ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1 lg:grid-cols-3'}`}>
          {poll.categories.map((category, index) => (
            <motion.div
              key={category.name}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              className="bg-white rounded-lg shadow-lg p-6 border border-gray-100"
              style={{
                minHeight: `${Math.max(200, (category.totalInputs || 0) * 20 + 200)}px`
              }}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-primary">{category.name}</h3>
                <div className="text-sm text-primary">
                  <span className="bg-blue-100 text-primary px-2 py-1 rounded-full">
                    {category.points || 0} Punkte
                  </span>
                </div>
              </div>

              {/* Point Button */}
              <div className="mb-4">
                <button
                  onClick={() => addPoint(category.name)}
                  disabled={!isRoundActive || remainingInputs === 0 || submitting}
                  className="w-full btn-primary flex items-center justify-center font-medium py-3 px-4 rounded-lg"
                >
                  <SafeIcon icon={FiPlus} className="mr-2" />
                  Punkt hinzufügen
                </button>
              </div>

              {/* Comment Section */}
              <div className="space-y-3">
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={comments[category.name] || ''}
                    onChange={(e) => updateComment(category.name, e.target.value)}
                    placeholder="Kommentar hinzufügen..."
                    disabled={!isRoundActive || remainingInputs === 0}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary disabled:bg-gray-100 text-primary"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        addComment(category.name);
                      }
                    }}
                  />
                  <button
                    onClick={() => addComment(category.name)}
                    disabled={!isRoundActive || remainingInputs === 0 || !comments[category.name]?.trim() || submitting}
                    className="btn-primary p-2 rounded-lg"
                  >
                    <SafeIcon icon={FiSend} />
                  </button>
                </div>

                {/* Display Comments */}
                {category.comments && category.comments.length > 0 && (
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    <h4 className="text-sm font-semibold text-primary flex items-center">
                      <SafeIcon icon={FiMessageSquare} className="mr-1" />
                      Kommentare ({category.comments.length})
                    </h4>
                    {category.comments.slice(-3).map((comment, commentIndex) => (
                      <div key={commentIndex} className="bg-gray-50 rounded p-2 text-sm">
                        <p className="text-primary">{comment.text}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(comment.timestamp).toLocaleTimeString()}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ParticipantView;